<script setup>
import { ref, onMounted, computed } from 'vue';
import { useCommonStore } from '@/stores/common';
import axios from 'axios';

const props = defineProps(['feedbackSource','backgroundColor']);
const store = useCommonStore();

const imgURL = ref(store.cloudfrontImgURL);
const clientFeedbackEndpoint = ref(store.clientFeedbackURL());
const feedbackSourceType = ref(props.feedbackSource);
const backgroundColor = ref(props.backgroundColor);
const clientComments = ref([]);
const clientCommentsNoNetwork = ref([
    {name: 'David Bridger',type: 'Private Client - Retired PwC Partner',comments: 'I am David Bridger, a chartered accountant and former PwC partner. I have been advised by clarity for about 30 years.  I particularly value having an annual meeting with my clarity Advisor covering a review of my and my family’s financial positions and being able to raise a whole range of specific financial issues personal to me. Amongst other things, clarity also advise me at least every 3 months about the collective funds they do and no longer recommend for investment. I have always followed their recommendations and have proven over several years that the investment gains made as a result of these changes, easily outweigh the fee consequences of taking up this element of Clarity’s services. Finally, I also value the ability to raise financial questions throughout the year and getting prompt personalised replies on such diverse subjects as ISA recommendations, Trust investments, SIPP investments, Life insurance policies, Capital Gains tax mitigation, etc.'},
    {name: 'Jonathan & Kathrin',type: 'Private Client - Retired PwC Partner',comments: 'Claire has advised me and my wife for many years. In that time she has kept our finances under control with annual meetings bringing some discipline to our affairs with a structured approach to portfolio management & fund selection. Claire has a nice down-to-earth approach. Each meeting starts with her understanding our goals and building flexibility into the planning as needed. Critically Claire anticipates the future and has brought things to our attention (in areas such as inheritance tax and pension planning) which would have been beyond us, but which were important in allowing us flexibility with retirement. Although we have chosen an arrangement with one annual meeting, Claire and her team keep us up to date with regulatory and similar changes and are also responsive to ad hoc queries and very happy to explain what can be obscure matters. Ultimately working with Claire is like working with a friend - always professional but with a sense of humour and a genuine interest in what we’re up to.'},
    {name: 'JT',type: 'Private Client - Retired PwC Partner',comments: 'I\'ve been supported by clarity for close to thirty years and I\'ve always found them to be highly professional, committed, informative and very supportive.  Their timely advice has really helped me through my professional life, through the birth of our children and now into retirement.  I value their financial insights and their commitment, including challenging me when I\'m about to follow the latest fad.'},
]);

// Index of the comment shown in the centre card (and the single mobile card)
const currentIndex = ref(0);
// How many places the three desktop cards have rotated (right arrow +1, left arrow -1)
const rotation = ref(0);

// Card slot positions. The cards keep their DOM order and only their slot classes change,
// so the CSS transition animates them between positions.
const slots = ['left', 'centre', 'right'];
const slotClasses = {
    left: 'z-0 top-[65px] left-0 w-[344px] h-[264px] opacity-85',
    centre: 'z-10 top-0 left-1/4 w-1/2 h-[377px] opacity-100',
    right: 'z-0 top-[65px] left-[calc(100%-344px)] w-[344px] h-[264px] opacity-85',
};
// The left card shows the next comment and the right card the previous one
const slotCommentOffset = { left: 1, centre: 0, right: -1 };

const clientFeedbackBackground = computed(() => `url('${imgURL.value}client-feedback-background.svg')`);
const clientFeedbackBlueBackground = computed(() => `url('${imgURL.value}client-feedback-background-blue.png')`);

function wrapIndex(index, length) {
    return ((index % length) + length) % length;
}

function getComment(offset) {
    const count = clientComments.value.length;
    if(count === 0) {
        return {name: '', type: '', comments: ''};
    }
    return clientComments.value[wrapIndex(currentIndex.value + offset, count)];
}

const cards = computed(() => [0, 1, 2].map(cardNumber => {
    const slot = slots[wrapIndex(cardNumber + rotation.value, slots.length)];
    return {
        id: cardNumber,
        slot,
        isCentre: slot === 'centre',
        comment: getComment(slotCommentOffset[slot]),
    };
}));

const singleComment = computed(() => getComment(0));

function loadClientComments() {
    axios.get(clientFeedbackEndpoint.value)
        .then(res => {
            const comments = res.data;
            if(feedbackSourceType.value === 'pwc') {
                clientComments.value = comments.filter(elm => elm.type.includes('PwC') || elm.type.includes('PricewaterhouseCoopers'));
            } else {
                clientComments.value = comments;
            }
        })
        .catch(err => {
            console.log(err);
        })
        .finally(() => {
            if(clientComments.value.length === 0) {
                clientComments.value = clientCommentsNoNetwork.value;
            }
            currentIndex.value = 0;
        });
}

function cardRight() {
    rotation.value++;
    currentIndex.value++;
}

function cardLeft() {
    rotation.value--;
    currentIndex.value--;
}

const headerColorClass = computed(() => backgroundColor.value === 'blue' ? 'clarity-h3-white' : 'clarity-h3-blue');
const backgroundImage = computed(() => backgroundColor.value === 'blue' ? clientFeedbackBlueBackground.value : clientFeedbackBackground.value);

const arrowClasses = 'absolute z-11 flex items-center justify-center w-10 h-10 cursor-pointer rounded-full bg-(--clarity-pure-white) border border-(--clarity-grey)';
const cardBaseClasses = 'bg-(--clarity-pure-white) shadow-[0px_0px_10px_#0000001A] text-center overflow-hidden p-[30px]';
const blurTextClasses = 'text-transparent! [text-shadow:0_0_5px_rgba(0,0,0,0.5)]';

onMounted(() => {
    loadClientComments();
});

</script>

<template>
    <div class="max-w-full h-[745px]" :style="{ backgroundImage }">
        <div class="hidden md:block">
            <div v-if="feedbackSourceType === 'all'">
                <h3 :class="headerColorClass" class="clarity-padding-top-2el clarity-padding-bottom-m text-center">WHAT OUR CLIENTS SAY</h3>
            </div>
            <div v-if="feedbackSourceType === 'pwc'">
                <h3 class="clarity-h3-blue clarity-padding-top-2el clarity-padding-bottom-m text-center">WHAT OTHER P<span class="lowercase!">W</span>C CLIENTS SAY</h3>
            </div>
            <div class="relative mx-[15%]">
                <div
                    v-for="card in cards"
                    :key="card.id"
                    :class="[cardBaseClasses, slotClasses[card.slot]]"
                    class="absolute rounded-[27px] transition-all duration-600 ease-in-out"
                >
                    <img :class="{ 'blur-[4px]': !card.isCentre }" class="w-[90px] h-[90px] mx-auto mb-5" :src="imgURL+'client-picture.png'" alt="Client Image">
                    <h4 :class="{ [blurTextClasses]: !card.isCentre }" class="clarity-h4-blue text-center mb-2.5!">{{ card.comment.name }}</h4>
                    <p :class="{ [blurTextClasses]: !card.isCentre }" class="clarity-small-grey-text mb-[15px]!">{{ card.comment.type }}</p>
                    <ScrollPanel :class="{ 'h-[150px] mx-5': card.isCentre }">
                        <p :class="{ [blurTextClasses]: !card.isCentre }" class="clarity-body-text-grey">{{ card.comment.comments }}</p>
                    </ScrollPanel>
                </div>
                <button type="button" @click="cardLeft" :class="arrowClasses" class="left-[23%] top-[180px]" aria-label="Previous review"><span class="material-symbols-outlined">chevron_left</span></button>
                <button type="button" @click="cardRight" :class="arrowClasses" class="right-[23%] top-[180px]" aria-label="Next review"><span class="material-symbols-outlined">chevron_right</span></button>
            </div>
        </div>
        <div class="md:hidden mx-auto">
            <div v-if="feedbackSourceType === 'all'">
                <h3 :class="headerColorClass" class="clarity-padding-top-2el clarity-padding-bottom-m text-center">WHAT OUR CLIENTS SAY</h3>
            </div>
            <div v-if="feedbackSourceType === 'pwc'">
                <h3 class="clarity-h3-blue clarity-padding-top-2el clarity-padding-bottom-m text-center">WHAT OTHER P<span class="lowercase!">W</span>C CLIENTS SAY</h3>
            </div>

            <button type="button" @click="cardLeft" :class="arrowClasses" class="left-[9%] mt-[168px]" aria-label="Previous review"><span class="material-symbols-outlined">chevron_left</span></button>

            <div :class="cardBaseClasses" class="absolute z-10 left-[12%] right-[12%] h-[377px] rounded-md">
                <img class="w-[90px] h-[90px] mx-auto mb-5" :src="imgURL+'client-picture.png'" alt="Client Image">
                <h4 class="clarity-h4-blue text-center mb-2.5!">{{ singleComment.name }}</h4>
                <p class="clarity-small-grey-text">{{ singleComment.type }}</p>
                <ScrollPanel class="h-[150px] mx-5">
                    <p class="clarity-body-text-grey">{{ singleComment.comments }}</p>
                </ScrollPanel>
            </div>

            <button type="button" @click="cardRight" :class="arrowClasses" class="right-[9%] mt-[168px]" aria-label="Next review"><span class="material-symbols-outlined">chevron_right</span></button>
        </div>
    </div>
</template>
